/** ClasificacionTab — Árbol jerárquico de 5 niveles para clasificar tickets.
 *
 * Niveles: 1=Grupo, 2=Categoría, 3=Subcategoría 1, 4=Subcategoría 2, 5=Subcategoría 3.
 * Cada nodo puede tener N hijos del nivel siguiente. La ruta_completa se calcula
 * en el backend al crear/editar.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../../components/ui/dialog';
import {
  Plus, Pencil, Trash2, Loader2, ChevronRight, ChevronDown, FolderTree,
  Upload, Download, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';

const NIVEL_LABELS = {
  1: 'Grupo',
  2: 'Categoría',
  3: 'Subcategoría 1',
  4: 'Subcategoría 2',
  5: 'Subcategoría 3',
};

export default function ClasificacionTab({ api }) {
  const { user } = useAuth();
  const isAdmin = ['ADMINISTRADOR', 'SUPERADMIN'].includes(user?.rol);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: '', parent_id: null, nivel: 1, activo: true, orden: 0 });
  const [saving, setSaving] = useState(false);

  // Import / Export
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);    // { creados, actualizados, total_filas }
  const [importError, setImportError] = useState(null);      // { error, fila, columna, detalle }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets/clasificacion/tree');
      setItems(data);
    } catch (e) { toast.error('Error cargando clasificación'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Construir mapa parent -> [hijos] para render eficiente
  const byParent = {};
  for (const it of items) {
    const key = it.parent_id || 'root';
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(it);
  }
  Object.values(byParent).forEach(arr => arr.sort((a, b) => a.orden - b.orden));

  const toggle = (id) => {
    setExpanded((prev) => {
      const ns = new Set(prev);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };

  const openCreate = (parent = null) => {
    setEditing(null);
    setForm({
      nombre: '',
      parent_id: parent?.id || null,
      nivel: parent ? parent.nivel + 1 : 1,
      activo: true,
      orden: 0,
    });
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nombre: it.nombre,
      parent_id: it.parent_id,
      nivel: it.nivel,
      activo: it.activo,
      orden: it.orden,
    });
    setDialogOpen(true);
  };
  const remove = async (it) => {
    const tieneHijos = byParent[it.id]?.length > 0;
    const msg = tieneHijos
      ? `"${it.nombre}" tiene clasificaciones hijas que también serán eliminadas. ¿Continuar?`
      : `¿Eliminar "${it.nombre}"?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/tickets/clasificacion/${it.id}`); toast.success('Eliminado'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error eliminando'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, nivel: parseInt(form.nivel, 10), orden: parseInt(form.orden || 0, 10) };
      if (editing) await api.put(`/tickets/clasificacion/${editing.id}`, payload);
      else await api.post('/tickets/clasificacion', payload);
      toast.success(editing ? 'Actualizado' : 'Creado');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando'); }
    finally { setSaving(false); }
  };

  // ============ Import / Export ============
  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    // Reset input para permitir elegir el mismo archivo de nuevo
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const confirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      const { data } = await api.post('/tickets/admin/clasificacion/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      toast.success(`Importación completada: ${data.creados} creados, ${data.actualizados} actualizados`);
      setPendingFile(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error) {
        setImportError(detail);
      } else {
        setImportError({ error: typeof detail === 'string' ? detail : 'Error desconocido durante la importación.' });
      }
      setPendingFile(null);
    } finally {
      setImporting(false);
    }
  };

  const cancelImport = () => {
    setPendingFile(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/tickets/admin/clasificacion/export', { responseType: 'blob' });
      // Filename desde Content-Disposition o default
      let filename = `clasificacion_tickets_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i.exec(cd);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
      }
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Archivo descargado');
    } catch (e) {
      toast.error('Error al exportar: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  const Node = ({ it, depth }) => {
    const hijos = byParent[it.id] || [];
    const isOpen = expanded.has(it.id);
    const puedeTenerHijos = it.nivel < 5;
    return (
      <div data-testid={`clasif-${it.id}`}>
        <div
          className="flex items-center gap-2 px-2 py-2 hover:bg-slate-50 border-b"
          style={{ paddingLeft: `${depth * 24 + 8}px` }}
        >
          {hijos.length > 0 ? (
            <button onClick={() => toggle(it.id)} className="p-0.5 hover:bg-slate-200 rounded">
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : <span className="w-5" />}
          <span className="text-xs font-mono text-slate-400">N{it.nivel}</span>
          <span className="text-sm flex-1">{it.nombre}</span>
          {!it.activo && <span className="text-xs text-slate-400 italic">inactivo</span>}
          {isAdmin && puedeTenerHijos && (
            <Button size="icon" variant="ghost" onClick={() => openCreate(it)} title={`Agregar ${NIVEL_LABELS[it.nivel + 1]}`} data-testid={`add-child-${it.id}`}>
              <Plus className="w-4 h-4 text-emerald-600" />
            </Button>
          )}
          {isAdmin && (
            <>
              <Button size="icon" variant="ghost" onClick={() => openEdit(it)}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => remove(it)}>
                <Trash2 className="w-4 h-4 text-rose-500" />
              </Button>
            </>
          )}
        </div>
        {isOpen && hijos.map((c) => <Node key={c.id} it={c} depth={depth + 1} />)}
      </div>
    );
  };

  const roots = byParent['root'] || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-600 flex items-center gap-1"><FolderTree className="w-4 h-4" /> Árbol de 5 niveles: Grupo → Categoría → Sub 1 → Sub 2 → Sub 3.</p>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={onFileChosen}
                className="hidden"
                data-testid="input-import-clasificacion"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                data-testid="btn-import-clasificacion"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Importar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
                data-testid="btn-export-clasificacion"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Exportar
              </Button>
              <Button onClick={() => openCreate(null)} size="sm" data-testid="btn-nuevo-grupo">
                <Plus className="w-4 h-4 mr-1" /> Nuevo grupo
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          {roots.length === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center">Sin clasificaciones. Cree un grupo para empezar.</div>}
          {roots.map((r) => <Node key={r.id} it={r} depth={0} />)}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Editar ${NIVEL_LABELS[form.nivel]}` : `Nuevo ${NIVEL_LABELS[form.nivel] || ''}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Nivel <strong>{form.nivel}</strong> · {NIVEL_LABELS[form.nivel]}
              {form.parent_id && <> · Padre: {items.find(i => i.id === form.parent_id)?.nombre}</>}
            </div>
            <div>
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Facturación" />
            </div>
            <div>
              <Label>Orden</Label>
              <Input type="number" value={form.orden} onChange={(e) => setForm({ ...form, orden: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
              <Label>Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.nombre.trim()} data-testid="btn-guardar-clasif">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de importación */}
      <Dialog open={!!pendingFile} onOpenChange={(o) => !o && cancelImport()}>
        <DialogContent data-testid="dialog-import-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-600" />
              Importar clasificaciones
            </DialogTitle>
            <DialogDescription>
              Se procesará el archivo <strong className="text-slate-900">{pendingFile?.name}</strong>. Las clasificaciones existentes se <strong>actualizarán</strong> y las nuevas se <strong>insertarán</strong>. Las clasificaciones que existan en la base de datos pero no en el archivo <strong>permanecerán intactas</strong>. Si algún registro falla, se cancela toda la importación.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelImport} disabled={importing} data-testid="btn-cancel-import">
              Cancelar
            </Button>
            <Button onClick={confirmImport} disabled={importing} data-testid="btn-confirm-import">
              {importing && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resultado exitoso */}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent data-testid="dialog-import-result">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              Importación completada
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 my-2">
            <div className="border rounded-md p-3 text-center bg-emerald-50">
              <div className="text-2xl font-bold text-emerald-700" data-testid="stat-creados">{importResult?.creados ?? 0}</div>
              <div className="text-xs text-emerald-700 uppercase tracking-wider">Creados</div>
            </div>
            <div className="border rounded-md p-3 text-center bg-blue-50">
              <div className="text-2xl font-bold text-blue-700" data-testid="stat-actualizados">{importResult?.actualizados ?? 0}</div>
              <div className="text-xs text-blue-700 uppercase tracking-wider">Actualizados</div>
            </div>
            <div className="border rounded-md p-3 text-center bg-slate-50">
              <div className="text-2xl font-bold text-slate-700" data-testid="stat-errores">{importResult?.errores ?? 0}</div>
              <div className="text-xs text-slate-700 uppercase tracking-wider">Errores</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 text-center">
            Total de filas procesadas: <strong>{importResult?.total_filas ?? 0}</strong>
          </div>
          <DialogFooter>
            <Button onClick={() => setImportResult(null)} data-testid="btn-cerrar-result">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error de importación */}
      <Dialog open={!!importError} onOpenChange={(o) => !o && setImportError(null)}>
        <DialogContent data-testid="dialog-import-error">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              Error en la importación
            </DialogTitle>
            <DialogDescription>
              Se canceló toda la operación. Ningún registro fue creado ni actualizado.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            {(importError?.fila || importError?.columna) && (
              <div className="font-medium mb-1">
                {importError.fila ? `Fila ${importError.fila}` : ''}{importError.fila && importError.columna ? ', ' : ''}{importError.columna ? `columna "${importError.columna}"` : ''}
              </div>
            )}
            <div data-testid="error-detalle">{importError?.detalle || importError?.error}</div>
          </div>
          <DialogFooter>
            <Button onClick={() => setImportError(null)} data-testid="btn-cerrar-error">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
